(function () {
  const ATTR_GIFT = 'Gratiszugabe';
  const ATTR_PACK = 'Packhinweis';
  const AUTO_NOTE = '(wird automatisch in der Lieferung enthalten sein)';
  let attributeSyncing = false;
  let lastGiftPayload = null;
  let attributeTimer = null;

  const formatMoney = (cents, format) => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = Number(cents) || 0;
    const formatString = format || window.KROWN?.settings?.shop_money_format || '€{{amount_with_comma_separator}}';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    const formatWithDelimiters = (number, precision, thousands, decimal) => {
      thousands = thousands || ',';
      decimal = decimal || '.';
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? decimal + parts[1] : '';
      return dollarsAmount + centsAmount;
    };

    const match = formatString.match(placeholderRegex);
    if (!match) return (cents / 100).toFixed(2).replace('.', ',') + ' €';

    let value = '';
    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2, '.', ',');
    }
    return formatString.replace(placeholderRegex, value);
  };

  const escapeHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  const stripTags = (html) => String(html || '').replace(/<[^>]+>/g, '');

  const interpolate = (template, vars) => {
    let text = template || '';
    Object.keys(vars).forEach((key) => {
      text = text.replaceAll(`{{ ${key} }}`, vars[key]).replaceAll(`{{${key}}}`, vars[key]);
    });
    return text;
  };

  const parseConfig = (root) => {
    const host = root.closest('.terjung-cart-rewards-host') || root.parentElement;
    const script = host?.querySelector('[data-terjung-rewards-config]')
      || root.querySelector('[data-terjung-rewards-config]');
    if (!script) return null;
    try {
      const config = JSON.parse(script.textContent);
      config.steps = (config.steps || [])
        .filter((step) => Number(step.amount) > 0)
        .sort((a, b) => Number(a.amount) - Number(b.amount));
      return config;
    } catch (error) {
      return null;
    }
  };

  const unlockedGifts = (paidCents) => {
    const parts = [];
    if (paidCents >= 5000) parts.push('2 Brötchen');
    else if (paidCents >= 2500) parts.push('1 Brötchen');
    if (paidCents >= 9000) parts.push('1 Brot');
    return parts;
  };

  const giftAttributeValue = (paidCents) => {
    const parts = unlockedGifts(paidCents);
    if (!parts.length) return '';
    return parts.join(' + ') + ' (Zufallsauswahl)';
  };

  const packHintValue = (paidCents) => {
    const gift = giftAttributeValue(paidCents);
    if (!gift) return '';
    return 'Bitte beilegen: ' + gift;
  };

  const nextGoal = (config, paidCents) => {
    for (const step of config.steps) {
      const amount = Number(step.amount);
      if (paidCents < amount) {
        return {
          type: step.kind === 'shipping' ? 'shipping' : 'gift',
          key: step.key,
          amount,
          remaining: amount - paidCents,
          label: step.label,
        };
      }
    }
    return { type: 'complete' };
  };

  const markerClass = (key) => {
    if (key === 'roll') return 'is-roll';
    if (key === 'rolls') return 'is-rolls';
    if (key === 'shipping') return 'is-shipping';
    if (key === 'bread') return 'is-bread';
    return '';
  };

  /** Equal visual slots in bar quarters — not proportional to € gaps. */
  const visualPositions = (stepCount) => {
    const n = Math.max(1, stepCount);
    const positions = [];
    for (let i = 0; i < n; i += 1) {
      positions.push(((i + 0.5) / n) * 100);
    }
    return positions;
  };

  const visualProgress = (config, paidCents) => {
    const amounts = [0, ...config.steps.map((step) => Number(step.amount))];
    const visuals = [0, ...visualPositions(config.steps.length)];
    if (paidCents <= 0) return 0;
    if (paidCents >= amounts[amounts.length - 1]) return 100;

    for (let i = 0; i < amounts.length - 1; i += 1) {
      const a0 = amounts[i];
      const a1 = amounts[i + 1];
      if (paidCents <= a1) {
        const span = Math.max(1, a1 - a0);
        const t = (paidCents - a0) / span;
        return visuals[i] + t * (visuals[i + 1] - visuals[i]);
      }
    }
    return 100;
  };

  const reachedKeys = (config, paidCents) => config.steps
    .filter((step) => paidCents >= Number(step.amount))
    .map((step) => step.key);

  const buildMessage = (config, paidCents) => {
    const goal = nextGoal(config, paidCents);
    const msgs = config.messages || {};
    if (goal.type === 'complete') return msgs.complete || 'Alle Zugaben erreicht.';

    const remaining = `<strong>${formatMoney(goal.remaining, config.moneyFormat)}</strong>`;
    if (goal.key === 'roll') return interpolate(msgs.toRoll, { remaining_amount: remaining });
    if (goal.key === 'rolls') return interpolate(msgs.toTwoRolls, { remaining_amount: remaining });
    if (goal.key === 'shipping') return interpolate(msgs.toShipping, { remaining_amount: remaining });
    if (goal.key === 'bread') {
      const shippingDone = paidCents >= Number(config.shipping || 6000);
      const prefix = shippingDone ? (msgs.shippingUnlocked ? msgs.shippingUnlocked + ' ' : '') : '';
      return prefix + interpolate(msgs.toBread, { remaining_amount: remaining });
    }
    return interpolate(msgs.toShipping, { remaining_amount: remaining });
  };

  const burstConfetti = (root) => {
    const layer = document.createElement('div');
    layer.className = 'terjung-cart-rewards__confetti';
    layer.setAttribute('aria-hidden', 'true');
    const colors = ['#f49818', '#20160c', '#d97812', '#ffe0b8', '#8a4b12'];
    for (let i = 0; i < 12; i += 1) {
      const speck = document.createElement('span');
      const angle = (Math.PI * 2 * i) / 12;
      const distance = 26 + (i % 4) * 8;
      speck.style.left = `${42 + (i % 5) * 4}%`;
      speck.style.background = colors[i % colors.length];
      speck.style.setProperty('--dx', `${Math.cos(angle) * distance}px`);
      speck.style.setProperty('--dy', `${Math.sin(angle) * distance + 16}px`);
      speck.style.setProperty('--rot', `${(i * 42) % 360}deg`);
      speck.style.animationDelay = `${(i % 4) * 16}ms`;
      layer.appendChild(speck);
    }
    root.appendChild(layer);
    window.setTimeout(() => layer.remove(), 850);
  };

  const patchBar = (root, config, paidCents, options = {}) => {
    const progress = visualProgress(config, paidCents);
    const message = buildMessage(config, paidCents);
    const goal = nextGoal(config, paidCents);
    const autoNote = config.autoNote || AUTO_NOTE;
    const positions = visualPositions(config.steps.length);
    const nextReached = reachedKeys(config, paidCents);
    const previousReached = Array.isArray(root._terjungReached) ? root._terjungReached : nextReached;
    const newlyUnlocked = options.animate
      && nextReached.some((key) => !previousReached.includes(key));

    root.classList.toggle('is-complete', goal.type === 'complete');

    let fill = root.querySelector('.terjung-cart-rewards__fill');
    let track = root.querySelector('.terjung-cart-rewards__track');
    let messageEl = root.querySelector('.terjung-cart-rewards__message');
    let noteEl = root.querySelector('.terjung-cart-rewards__auto-note');
    let ovenEl = root.querySelector('.terjung-cart-oven-hint');

    // Fast path: SSR / previous render exists — only patch values (no full rebuild).
    if (fill && track && messageEl && noteEl) {
      fill.style.width = `${progress}%`;
      messageEl.innerHTML = message;
      noteEl.textContent = autoNote;
      track.setAttribute('aria-label', stripTags(message));

      const markers = track.querySelectorAll('.terjung-cart-rewards__marker');
      config.steps.forEach((step, index) => {
        const marker = markers[index];
        if (!marker) return;
        marker.style.left = `${positions[index]}%`;
        marker.classList.toggle('is-reached', paidCents >= Number(step.amount));
      });

      if (config.hasBread) {
        if (!ovenEl) {
          ovenEl = document.createElement('p');
          ovenEl.className = 'terjung-cart-oven-hint';
          ovenEl.textContent = config.ovenHint || '';
          root.appendChild(ovenEl);
        } else {
          ovenEl.textContent = config.ovenHint || '';
        }
      } else if (ovenEl) {
        ovenEl.remove();
      }

      root._terjungReached = nextReached;
      if (newlyUnlocked) burstConfetti(root);
      return;
    }

    const markersHtml = config.steps.map((step, index) => {
      const amount = Number(step.amount);
      const shipping = step.kind === 'shipping';
      const label = shipping ? `${Math.round(amount / 100)}\u00a0€` : step.label;
      return `
        <span class="terjung-cart-rewards__marker ${markerClass(step.key)}${paidCents >= amount ? ' is-reached' : ''}" style="left:${positions[index]}%">
          <span class="terjung-cart-rewards__dot" aria-hidden="true"></span>
          <span class="terjung-cart-rewards__caption">${escapeHtml(label)}</span>
        </span>
      `;
    }).join('');

    root.innerHTML = `
      <div class="terjung-cart-rewards__track" role="img" aria-label="${escapeHtml(stripTags(message))}">
        <div class="terjung-cart-rewards__fill" style="width:${progress}%"></div>
        ${markersHtml}
      </div>
      <p class="terjung-cart-rewards__message">${message}</p>
      <p class="terjung-cart-rewards__auto-note">${escapeHtml(autoNote)}</p>
      ${config.hasBread ? `<p class="terjung-cart-oven-hint">${escapeHtml(config.ovenHint || '')}</p>` : ''}
    `;

    root._terjungReached = nextReached;
    if (newlyUnlocked) burstConfetti(root);
  };

  const PACK_NOTE_PREFIX = '【Packhinweis】';

  const stripPackBlock = (note) => String(note || '')
    .replace(/^【Packhinweis】[^\n]*\n*/m, '')
    .replace(/^---\s*\n*/m, '')
    .trim();

  const mergePackNote = (existingNote, pack) => {
    const customer = stripPackBlock(existingNote);
    if (!pack) return customer;
    const block = `${PACK_NOTE_PREFIX}${pack}`;
    return customer ? `${block}\n\n${customer}` : block;
  };

  const syncNoteFields = (note) => {
    document.querySelectorAll('#CartDrawer-Note, #CartPage-Note, textarea[name="note"]').forEach((el) => {
      if (document.activeElement === el) return;
      el.value = note;
    });
  };

  const syncAttributes = async (paidCents) => {
    const gift = giftAttributeValue(paidCents);
    const pack = packHintValue(paidCents);
    if (attributeSyncing) return;

    attributeSyncing = true;
    try {
      // Need current note so we don't wipe a customer delivery hint.
      const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then((r) => r.json());
      const currentGift = (cart.attributes && cart.attributes[ATTR_GIFT]) || '';
      const currentPack = (cart.attributes && cart.attributes[ATTR_PACK]) || '';
      const nextNote = mergePackNote(cart.note || '', pack);
      const payloadKey = `${gift}|${pack}|${nextNote}`;

      if (
        payloadKey === lastGiftPayload
        || (currentGift === gift && currentPack === pack && (cart.note || '') === nextNote)
      ) {
        lastGiftPayload = payloadKey;
        return;
      }

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          note: nextNote,
          attributes: {
            [ATTR_GIFT]: gift,
            [ATTR_PACK]: pack,
          },
        }),
      });
      lastGiftPayload = payloadKey;
      syncNoteFields(nextNote);
    } catch (error) {
      console.warn('Terjung cart rewards attribute sync failed', error);
    } finally {
      attributeSyncing = false;
    }
  };

  const scheduleAttributeSync = (paidCents) => {
    window.clearTimeout(attributeTimer);
    attributeTimer = window.setTimeout(() => {
      syncAttributes(paidCents);
    }, 900);
  };

  const readPaidFromDom = () => {
    const root = document.querySelector('terjung-cart-rewards');
    if (!root) return null;
    const config = root.config || parseConfig(root);
    if (!config) return null;
    const paid = Number(root.getAttribute('data-paid-total') || config.paidTotal || 0);
    return { root, config, paid };
  };

  class TerjungCartRewards extends HTMLElement {
    connectedCallback() {
      this.config = parseConfig(this);
      if (!this.config) return;
      const paid = Number(this.getAttribute('data-paid-total') || this.config.paidTotal || 0);
      this.config.paidTotal = paid;
      // Keep SSR markup; only patch if JS needs to re-align positions / state.
      patchBar(this, this.config, paid, { animate: false });
      scheduleAttributeSync(paid);
    }
  }

  if (!customElements.get('terjung-cart-rewards')) {
    customElements.define('terjung-cart-rewards', TerjungCartRewards);
  }

  /**
   * Lightweight refresh: use DOM/SSR totals — no extra /cart.js roundtrip.
   * Attribute write is debounced so Plus/Minus stays snappy.
   */
  const refreshVisibleBars = (paidOverride) => {
    const nodes = document.querySelectorAll('terjung-cart-rewards');
    if (!nodes.length) return;

    let paid = typeof paidOverride === 'number' ? paidOverride : null;
    if (paid === null) {
      const fromDom = readPaidFromDom();
      paid = fromDom ? fromDom.paid : 0;
    }

    nodes.forEach((root) => {
      const config = root.config || parseConfig(root);
      if (!config) return;
      root.config = config;
      config.paidTotal = paid;
      root.setAttribute('data-paid-total', String(paid));
      patchBar(root, config, paid, { animate: true });
    });

    scheduleAttributeSync(paid);
  };

  window.TerjungCartRewards = window.TerjungCartRewards || {};
  window.TerjungCartRewards.refresh = refreshVisibleBars;

  const bindCartEvents = () => {
    const form = document.getElementById('AjaxCartForm');
    if (form && !form.dataset.terjungRewardsBound) {
      form.dataset.terjungRewardsBound = 'true';
      form.addEventListener('cart-updated', () => {
        // Section HTML already replaced with correct SSR bar — only sync pack notes.
        const fromDom = readPaidFromDom();
        if (fromDom) scheduleAttributeSync(fromDom.paid);
      });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindCartEvents);
  } else {
    bindCartEvents();
  }
})();
